import React, { lazy, Suspense } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import mapValues from 'lodash/mapValues';
import ceil from 'lodash/ceil';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';

import NotFound from 'Components/NotFound/NotFound';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import Loading from 'Components/Utility/Loading';
import { currencyCode } from 'Helpers/Geo/data';
import { combineProductRegions } from 'Features/PickAndMix/helpers/pick-and-mix-helper';
import PickAndMixProductView from 'Features/PickAndMix/components/Views/PickAndMixProductView';
import useFetchPickAndMix from 'Features/PickAndMix/hooks/useFetchPickAndMix';
import { canConvertPickAndMixToBundle, convertPickAndMixToBundle } from 'Features/PickAndMix/helpers/convert-pick-and-mix-to-bundle';

const BundlePage = lazy(() => import(/* webpackChunkName: "BundleView" */ 'Features/Bundle/components/Views/BundleView'));

const PickAndMixProductPage = ({
  match: { params: { slug, id } },
}) => {
  const preselected = getUrlParameter('preselected');
  const preselectedSlugs = preselected && preselected.split(',');
  const { pickAndMix, notFound, redirect } = useFetchPickAndMix({ slug, id });
  const canConvertToBundle = canConvertPickAndMixToBundle(pickAndMix, preselectedSlugs);

  if (notFound) {
    return (
      <NotFound topSellersFilter="bundle" />
    );
  }

  if (redirect) {
    return (
      <NotFound redirect={redirect} topSellersFilter="bundle" />
    );
  }

  if (!pickAndMix) {
    return <Loading />;
  }

  if (preselected && canConvertToBundle) {
    const product = convertPickAndMixToBundle(pickAndMix, preselectedSlugs);
    return (
      <Suspense>
        <BundlePage
          preSelectedPnm
          product={product}
          selectTier={1}
        />
      </Suspense>
    );
  }

  const pnmProducts = get(pickAndMix, 'products', []);
  const preselectedProducts = (pnmProducts || []).filter(p => preselectedSlugs.includes(p.slug));
  const tiers = get(pickAndMix, 'tiers');
  const quantities = tiers?.map(tier => tier.quantity) || [];
  const preSelectedProductsLength = get(preselectedProducts, 'length');
  const tierMatch = quantities.includes(preSelectedProductsLength);


  if (preselected && tierMatch) {
    const quantity = preselectedProducts.length;
    let fullPrice = 0;

    preselectedProducts.forEach((p) => {
      fullPrice += p.price[currencyCode];
      p.display = true; // eslint-disable-line no-param-reassign
    });

    if (!quantity) {
      return [];
    }

    tiers.forEach((t) => {
      t.baseQuantity = t.quantity; // eslint-disable-line no-param-reassign
      t.basePrice = { ...t.price }; // eslint-disable-line no-param-reassign
    });

    const highestActiveTier = orderBy(tiers, 'quantity', 'desc').find(t => quantity >= t.quantity);

    if (!highestActiveTier) {
      return [];
    }

    const adjustedTier = {
      ...highestActiveTier,
      quantity,
      price: mapValues(
        highestActiveTier.basePrice,
        v => ceil((v / highestActiveTier.baseQuantity) * quantity),
      ),
    };

    const platforms = preselectedProducts.map(pp => pp.platforms);
    let validPlatforms = {};

    if (platforms && platforms.length > 0) {
      const initialObj = { windows: true, mac: true, linux: true };
      const reducedResult = reduce(platforms, (acc, obj) =>
        mapValues(acc, (value, key) => value && obj[key]), initialObj);

      validPlatforms = mapValues(reducedResult, Boolean);
    }

    const { regionsExcluded, regionsIncluded } = combineProductRegions(preselectedProducts);


    const product = {
      name: pickAndMix.name,
      type: 'bundle',
      drm: pickAndMix.drm,
      cover: pickAndMix.cover_image,
      template_type: 'standard',
      tiered: false,
      regions_excluded: regionsExcluded,
      regions_included: regionsIncluded,
      seo: {
        desc: pickAndMix.description,
      },
      price: adjustedTier.price,
      fullPrice: { [currencyCode]: fullPrice },
      desc: pickAndMix.about,
      platforms: validPlatforms,
      currentPrice: adjustedTier.price,
      img: [],
      bundles: [{
        displayTotal: preselectedProducts.length,
        downloadTotal: 0,
        filesTotal: 0,
        fullPrice: { [currencyCode]: fullPrice },
        games: preselectedProducts,
        incrementalFullPrice: fullPrice,
        price: adjustedTier.price,
      }],
    };
    return (
      <Suspense>
        <BundlePage
          preSelectedPnm
          product={product}
          selectTier={1}
        />
      </Suspense>
    );
  }

  return (
    <PickAndMixProductView pickAndMix={pickAndMix} />
  );
};

PickAndMixProductPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string,
      id: PropTypes.string,
    }),
  }).isRequired,
};

export default PickAndMixProductPage;
